import { IconSort, IconSortAsc, IconSortDesc } from '@myplayer/ui';
import { flexRender, type Table } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import {
  AUDIT_COLUMN_WIDTHS,
  AuditChainCell,
  AuditSnapshotCell,
  AuditWhatCell,
  AuditWhenCell,
  AuditWhoCell,
} from './audit-log-filter-bar.js';
import type { AuditEntryRecord } from './audit-log-helpers.js';
import {
  Button,
  Spinner,
  computeVirtualizedSlice,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildAuditEntryContextMenuItems } from './context-menu-items.js';
import { ADMIN_STRINGS } from './strings.js';

export const AUDIT_ROW_HEIGHT = 76;

export function AuditEntryCard(props: {
  readonly entry: AuditEntryRecord;
  readonly showWorkspaceBadge?: boolean | undefined;
  readonly workspaceName?: string | undefined;
  readonly onContextMenu?: ((e: React.MouseEvent) => void) | undefined;
}): ReactElement {
  const { entry } = props;
  return (
    <div
      data-testid={`audit-card-${entry.auditId}`}
      onContextMenu={props.onContextMenu}
      className="flex h-full flex-col justify-between gap-3 rounded-card border border-border bg-surface p-4 text-left shadow-card-base transition-colors hover:border-border-medium hover:bg-surface-subtle"
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          <AuditWhatCell
            entry={entry}
            showWorkspaceBadge={props.showWorkspaceBadge}
            workspaceName={props.workspaceName}
          />
        </div>
        <AuditChainCell entry={entry} />
      </div>
      <div className="grid grid-cols-2 gap-2.5 rounded-control border border-subtle bg-surface-subtle p-2.5">
        <div className="min-w-0 space-y-0.5">
          <span className="block text-[11px] font-semibold text-fg-muted">Before</span>
          <AuditSnapshotCell
            testId={`audit-before-${entry.auditId}`}
            snapshot={entry.beforeSnapshot}
          />
        </div>
        <div className="min-w-0 space-y-0.5">
          <span className="block text-[11px] font-semibold text-fg-muted">After</span>
          <AuditSnapshotCell
            testId={`audit-after-${entry.auditId}`}
            snapshot={entry.afterSnapshot}
            isAfter={true}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-subtle pt-2.5 text-[12px] text-fg-secondary">
        <div className="min-w-0 flex-1">
          <AuditWhoCell entry={entry} />
        </div>
        <AuditWhenCell timestamp={entry.timestamp} />
      </div>
    </div>
  );
}

export function AuditCardsView(props: {
  readonly entries: readonly AuditEntryRecord[];
  readonly totalCount: number;
  readonly loading: boolean;
  readonly showWorkspaceBadges: boolean;
  readonly workspaceNameMap: ReadonlyMap<string, string>;
  readonly onFilterBySearch?: ((query: string) => void) | undefined;
  readonly onFilterByAction?: ((action: string) => void) | undefined;
}): ReactElement {
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  return (
    <div className="min-w-0 space-y-2.5" data-testid="audit-cards-view">
      <div
        data-testid="audit-cards-virtual-scroll-container"
        className="max-h-[calc(100vh-220px)] overflow-y-auto"
      >
        {props.loading ? (
          <div
            role="status"
            data-testid="audit-log-loading-state"
            className="flex items-center justify-center gap-2.5 rounded-card border border-border bg-surface py-10 text-[13px] text-fg-secondary shadow-card-base"
          >
            <Spinner size={18} />
            <span>{ADMIN_STRINGS.loading.auditLog}</span>
          </div>
        ) : props.entries.length === 0 ? (
          <p
            data-testid="audit-log-empty-state"
            className="rounded-card border border-border bg-surface py-10 text-center text-[13px] text-fg-secondary shadow-card-base"
          >
            {ADMIN_STRINGS.empty.auditLog}
          </p>
        ) : (
          <ul
            aria-label="Audit entries list"
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
          >
            {props.entries.map((rec) => {
              const wsName = rec.workspaceId
                ? (props.workspaceNameMap.get(rec.workspaceId) ?? rec.workspaceId)
                : 'Organisation';
              return (
                <li key={rec.auditId}>
                  <AuditEntryCard
                    entry={rec}
                    showWorkspaceBadge={props.showWorkspaceBadges}
                    workspaceName={wsName}
                    onContextMenu={bindContextMenu(() =>
                      buildAuditEntryContextMenuItems({
                        entry: rec,
                        onFilterBySearch: props.onFilterBySearch,
                        onFilterByAction: props.onFilterByAction,
                      }),
                    )}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between rounded-card border border-border bg-surface px-4 py-2.5 text-[12px] text-fg-secondary shadow-card-base tabular-nums">
        <span data-testid="audit-pagination-summary">
          Showing {props.entries.length} of {props.totalCount} entries
        </span>
      </div>
      {contextMenuElement}
    </div>
  );
}

export function AuditTableView(props: {
  readonly table: Table<AuditEntryRecord>;
  readonly columnCount: number;
  readonly totalCount: number;
  readonly loading: boolean;
  readonly scrollTop: number;
  readonly viewportHeight: number;
  readonly onScrollChange: (scrollTop: number, clientHeight: number) => void;
  readonly onFilterBySearch?: ((query: string) => void) | undefined;
  readonly onFilterByAction?: ((action: string) => void) | undefined;
}): ReactElement {
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const allRows = props.table.getRowModel().rows;
  const virtualSlice = computeVirtualizedSlice({
    items: allRows,
    scrollTop: props.scrollTop,
    viewportHeight: props.viewportHeight,
    rowHeight: AUDIT_ROW_HEIGHT,
  });
  const renderSpacer = (h: number): ReactElement | null =>
    h > 0 ? (
      <tr aria-hidden="true">
        <td colSpan={props.columnCount} style={{ height: `${h}px`, padding: 0 }} />
      </tr>
    ) : null;

  return (
    <div className="overflow-hidden rounded-card border border-border bg-surface shadow-card-base">
      <div
        data-testid="audit-virtual-scroll-container"
        onScroll={(e) =>
          props.onScrollChange(e.currentTarget.scrollTop, e.currentTarget.clientHeight)
        }
        className="max-h-[calc(100vh-220px)] overflow-auto"
      >
        <table
          aria-label="Audit log table"
          className="w-full min-w-[980px] table-fixed border-collapse text-left text-[13px]"
        >
          <thead className="sticky top-0 z-10">
            {props.table.getHeaderGroups().map((hg) => (
              <tr
                key={hg.id}
                className="border-b border-subtle bg-surface-subtle text-[12px] text-fg-secondary"
              >
                {hg.headers.map((header) => {
                  const sortDir = header.column.getIsSorted();
                  const widthClass = AUDIT_COLUMN_WIDTHS[header.column.id] ?? '';
                  return (
                    <th key={header.id} className={`px-4 py-2.5 font-semibold ${widthClass}`}>
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="inline"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex cursor-pointer items-center gap-1 text-left font-semibold text-fg-secondary hover:text-fg"
                        >
                          <span className="sr-only">
                            Sort by {String(header.column.columnDef.header)}
                          </span>
                          <span aria-hidden="true">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {sortDir === 'asc' ? (
                            <IconSortAsc />
                          ) : sortDir === 'desc' ? (
                            <IconSortDesc />
                          ) : (
                            <IconSort className="opacity-50" />
                          )}
                        </Button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-subtle">
            {props.loading ? (
              <tr>
                <td
                  colSpan={props.columnCount}
                  role="status"
                  data-testid="audit-log-loading-state"
                  className="py-10 text-center text-[13px] text-fg-secondary"
                >
                  <div className="inline-flex items-center gap-2.5">
                    <Spinner size={18} />
                    <span>{ADMIN_STRINGS.loading.auditLog}</span>
                  </div>
                </td>
              </tr>
            ) : allRows.length === 0 ? (
              <tr>
                <td
                  colSpan={props.columnCount}
                  data-testid="audit-log-empty-state"
                  className="py-10 text-center text-[13px] text-fg-secondary"
                >
                  {ADMIN_STRINGS.empty.auditLog}
                </td>
              </tr>
            ) : (
              <>
                {renderSpacer(virtualSlice.topSpacerHeight)}
                {virtualSlice.visibleItems.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`audit-row-${row.original.auditId}`}
                    onContextMenu={bindContextMenu(() =>
                      buildAuditEntryContextMenuItems({
                        entry: row.original,
                        onFilterBySearch: props.onFilterBySearch,
                        onFilterByAction: props.onFilterByAction,
                      }),
                    )}
                    className="h-[76px] bg-surface transition-colors hover:bg-surface-subtle"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="h-[76px] px-4 py-2 align-middle">
                        <div className="flex max-h-[60px] flex-col justify-center overflow-hidden">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
                {renderSpacer(virtualSlice.bottomSpacerHeight)}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-subtle bg-surface px-4 py-2.5 text-[12px] text-fg-secondary tabular-nums">
        <span data-testid="audit-pagination-summary">
          Showing {virtualSlice.visibleItems.length} of {props.totalCount} entries
        </span>
      </div>
      {contextMenuElement}
    </div>
  );
}
